import React, { useMemo } from 'react';
import { FormattedDate, FormattedMessage, useIntl } from 'react-intl';
import { getCauses, getPreferredResolutions, getStatuses } from '@printdeal/business-logic';
import {
  Grid,
  Badge,
  Link,
  BadgeProps,
} from '@printdeal/ui-library';
import { Link as GatsbyLink } from 'gatsby';
import ComplaintDetailAttachments from './ComplaintDetailAttachments';
import { messages } from '../../../translations/complaint';
import { Complaint } from '../../../../../types/complaints';
import { colorToSemanticVariant } from '../../../../../helpers/brand';

const ComplaintDetail = ({ complaint }: { complaint: Complaint }) => {
  const intl = useIntl();
  const statuses = getStatuses();
  const { attachments, status } = complaint;
  const currentStatus = statuses.find((state) => status === state.mcpComplaintsKey);
  const causes = Object.values(getCauses());
  const categories = [...complaint.categories];
  const preferredResolutions = [...complaint.preferredCustomerResolutions];
  const resolutions = getPreferredResolutions();

  const getOrderLineIds = useMemo(() => (complaint.items
    .filter((item) => item.merchantItemId)
    .map((item) => item.merchantItemId)
  ), [complaint]);

  const mapCauses = useMemo(() => (causes
    .filter((cause) => categories
      .find((category) => category === cause.mappedMcpKey))
    .map((cause) => cause.translationKey.split('.')[2])
  ), [categories]);

  const mapResolutions = useMemo(() => (resolutions
    .filter((resolution) => preferredResolutions
      .find((preferredResolution) => preferredResolution === resolution.mappedMcpKey))
    .map((resolution) => resolution.translationKey.split('.')[2])
  ), [preferredResolutions]);

  return (
    <>
      {status && currentStatus && (
        <Grid className="pd-grid-cols-[2fr,4fr,2fr] pd-gap-2">
          {/* Status */}
          <Grid.Item className="pd-font-bold">
            <FormattedMessage
              id="complaints.details.status"
              defaultMessage="Status"
            />
          </Grid.Item>
          <Grid.Item>
            <Badge
              className="pd-mr-3"
              variant={colorToSemanticVariant(currentStatus.badgeColor) as BadgeProps['variant']}
              data-cy-status={currentStatus.name}
            >
              { intl.formatMessage(messages[currentStatus.mcpComplaintsKey]) }
            </Badge>
          </Grid.Item>
          {/* Date submitted */}
          <Grid.Item className="pd-col-start-1 pd-font-bold">
            <FormattedMessage
              id="complaints.details.submitted"
              defaultMessage="Date submitted"
            />
          </Grid.Item>
          <Grid.Item>
            <FormattedDate value={complaint.createdAt} />
          </Grid.Item>
          {/* Order */}
          <Grid.Item className="pd-col-start-1 pd-font-bold">
            <FormattedMessage
              id="complaints.details.order"
              defaultMessage="Order"
            />
          </Grid.Item>
          <Grid.Item>
            <FormattedMessage
              id="complaints.details.order.orderId"
              defaultMessage="Order {orderId}"
              values={{ orderId: complaint.merchantOrderId }}
            />
          </Grid.Item>
          <Grid.Item className="pd-text-right">
            <Link
              as={GatsbyLink}
              to={`../../orders/${complaint.orderUuid}`}
              data-cy="linkToOrder"
              variant="secondary"
            >
              <FormattedMessage id="complaints.linkToOrder" defaultMessage="See order" />
            </Link>
          </Grid.Item>
          {/* Orderline(s) */}
          <Grid.Item className="pd-font-bold">
            <FormattedMessage
              id="complaints.details.orderlines"
              defaultMessage="Orderline(s)"
            />
          </Grid.Item>
          <Grid.Item>
            {getOrderLineIds.join(', ')}
          </Grid.Item>
          {/* Type of complaint */}
          <Grid.Item className="pd-col-start-1 pd-font-bold">
            <FormattedMessage
              id="complaints.details.typeOfComplaint"
              defaultMessage="Type of complaint"
            />
          </Grid.Item>
          <Grid.Item>
            {causes && mapCauses
              .map((category) => intl.formatMessage(messages[category])).join(', ')}
          </Grid.Item>
          {/* Complaint description */}
          <Grid.Item className="pd-col-start-1 pd-font-bold">
            <FormattedMessage
              id="complaints.details.description"
              defaultMessage="Complaint description"
            />
          </Grid.Item>
          <Grid.Item>
            {complaint.description}
          </Grid.Item>
          {/* Desired resolution */}
          <Grid.Item className="pd-col-start-1 pd-font-bold">
            <FormattedMessage
              id="complaints.details.resolution"
              defaultMessage="Desired resolution"
            />
          </Grid.Item>
          <Grid.Item>
            {resolutions && mapResolutions
              .map((resolution) => intl.formatMessage(messages[resolution])).join(', ')}
          </Grid.Item>
          {/* Final resolution */}
          {complaint.finalCustomerResolutions.length > 0 && (
            <>
              <Grid.Item className="pd-col-start-1 pd-font-bold">
                <FormattedMessage
                  id="complaints.details.finalresolution"
                  defaultMessage="Final resolution"
                />
              </Grid.Item>
              <Grid.Item>
                {resolutions && mapResolutions
                  .map((resolution) => intl.formatMessage(messages[resolution])).join(', ')}
              </Grid.Item>
            </>
          )}
        </Grid>
      )}
      {(complaint.status !== 'resolved' || attachments.length > 0) && (
        <ComplaintDetailAttachments
          complaint={complaint}
          attachments={attachments}
        />
      )}
    </>
  );
};

export default ComplaintDetail;
